import React from "react";
import { useAuth } from "../components/Authentication";
import { Navigate } from "react-router-dom";
import {
  clearRedirectUrl,
  isAdminUser,
  isBuilderUser,
  isPartnerAdminUser,
  isPartnerUser,
  isTesterUser,
  saveRedirectUrl,
} from "../utils/AuthUtils";
import { useFeatureFlag, FeatureFlag } from "../utils/featureFlags";

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminRequired?: boolean;
  builderRequired?: boolean;
  partnerAdminRequired?: boolean;
  notAPartnerRequired?: boolean;
  testerRequired?: boolean;
}

/**
 * A protected route only works with a valid user from Authentication
 */
export const ProtectedRoute = ({
  children,
  adminRequired,
  builderRequired,
  partnerAdminRequired,
  notAPartnerRequired,
  testerRequired,
}: ProtectedRouteProps) => {
  const { user } = useAuth();

  if (!user) {
    // Redirect to login and save the URL
    saveRedirectUrl();
    return <Navigate to={"/login"} replace />;
  }

  if (
    (adminRequired && !isAdminUser(user)) || // if admin required and user is not admin
    (builderRequired && !isBuilderUser(user)) || // if builder required and user is not builder
    (partnerAdminRequired && !isPartnerAdminUser(user)) || // if partner admin required and user is not partneradmin
    (notAPartnerRequired && isPartnerUser(user)) || // if not a partner required and user is partner
    (testerRequired && !isTesterUser(user)) // tester required but not a tester
  ) {
    // Redirect to login but don't save the URL
    clearRedirectUrl();
    return <Navigate to={"/login"} replace />;
  }

  return <>{children}</>;
};

interface FeatureFlagProtectedRouteProps extends ProtectedRouteProps {
  featureFlag: FeatureFlag;
  fallbackPath?: string;
}

/**
 * A protected route that also checks for feature flag enablement
 */
export const FeatureFlagProtectedRoute = ({
  children,
  featureFlag,
  fallbackPath = "/",
  ...protectedRouteProps
}: FeatureFlagProtectedRouteProps) => {
  const isFeatureEnabled = useFeatureFlag(featureFlag);
  const { user } = useAuth();

  if (!user || isPartnerUser(user) || !isFeatureEnabled) {
    return <Navigate to="/" replace />;
  }

  return <ProtectedRoute {...protectedRouteProps}>{children}</ProtectedRoute>;
};

export const RedirectHomeRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
